import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { type ServiceItem } from '@/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ROLE_KEYS = ['active', 'passive', 'both', 'give', 'receive'] as const;
type RoleKey = (typeof ROLE_KEYS)[number];

function isRoleKey(value: string | null): value is RoleKey {
  return ROLE_KEYS.includes(value as RoleKey);
}

function ServicePills({ service }: { service: ServiceItem }) {
  const { t } = useTranslation('common');
  const roleKey = isRoleKey(service.value) ? service.value : null;
  const pill = roleKey ? t(`pills.${roleKey}`) : null;

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {service.extra_cost && service.offered && (
        <Badge variant="outline" className="text-xs">
          {t('services.extra_cost_badge', { cost: service.extra_cost })}
        </Badge>
      )}
      {pill && service.offered && (
        <Badge
          variant="secondary"
          className={cn(
            'text-xs',
            pill === 'Active' || pill === 'Give'
              ? 'bg-primary/20 text-primary/80'
              : pill === 'Passive' || pill === 'Receive'
                ? 'bg-purple-600/20 text-purple-400'
                : 'bg-border text-muted-foreground',
          )}
        >
          {pill}
        </Badge>
      )}
    </div>
  );
}

function ServiceRow({
  service,
  index,
}: {
  service: ServiceItem;
  index: number;
}) {
  const isOffered = service.offered;
  const hasDescription = Boolean(service.description);

  const nameEl = (
    <span
      className={cn(
        'flex-1 text-left text-sm font-medium',
        !isOffered && 'text-muted-foreground line-through',
      )}
    >
      {service.name}
    </span>
  );

  if (!hasDescription) {
    return (
      <div
        className={cn(
          'flex items-center justify-between rounded-md border px-3 py-3',
          !isOffered && 'opacity-60',
        )}
      >
        {nameEl}
        <ServicePills service={service} />
      </div>
    );
  }

  return (
    <AccordionItem
      value={`service-${index}`}
      className={cn('rounded-md border px-3', !isOffered && 'opacity-60')}
    >
      <AccordionTrigger className="gap-2 py-3 hover:no-underline [&>svg]:shrink-0">
        {nameEl}
        <ServicePills service={service} />
      </AccordionTrigger>
      <AccordionContent className="text-sm text-muted-foreground">
        {service.description}
      </AccordionContent>
    </AccordionItem>
  );
}

function ServiceGrid({ services }: { services: ServiceItem[] }) {
  return (
    <Accordion type="multiple" className="grid grid-cols-2 gap-2">
      {services.map((service, i) => (
        <ServiceRow key={service.key} service={service} index={i} />
      ))}
    </Accordion>
  );
}

export function ServicesCard({ services }: { services: ServiceItem[] }) {
  const { t } = useTranslation('common');
  const [collapsed, setCollapsed] = useState(true);

  if (!services?.length) return null;

  const offered = services.filter((s) => s.offered);
  const unavailable = services.filter((s) => !s.offered);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('services.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {offered.length > 0 && <ServiceGrid services={offered} />}

        {unavailable.length > 0 && (
          <Collapsible
            open={!collapsed}
            onOpenChange={(open) => setCollapsed(!open)}
          >
            <CollapsibleTrigger asChild>
              <button className="mb-2 flex w-full items-center justify-between text-xs font-medium tracking-wide text-muted-foreground uppercase hover:text-foreground">
                <span>{t('services.not_offered')}</span>
                <span>{collapsed ? '+' : '-'}</span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <ServiceGrid services={unavailable} />
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
